import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { ThermometerSun, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

const SEVERITY_TO_PRIORITY = { Severe: "urgent", Medium: "high", Low: "medium" };

const blank = {
  system_type: "",
  is_working: "",
  severity: "",
  noticed_at: "",
  indoor_temp_f: "",
  system_age_years: "",
  last_maintenance_date: "",
  error_code: "",
  failure_description: "",
  contacted_insurance: "",
  insurance_details: "",
  insurance_followup: "",
  photo_paths: [],
};

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {["Yes", "No"].map(v => (
        <button key={v} type="button" data-testid={`${testid}-${v.toLowerCase()}`} onClick={() => onChange(v)}
          className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-all ${value === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}
        >{v}</button>
      ))}
    </div>
  );
}

function Q({ num, text, required, children, gate }) {
  if (gate === false) return null;
  return (
    <div className="space-y-2 py-3 border-b border-slate-100 last:border-b-0">
      <Label className="text-sm text-slate-800 leading-snug flex gap-1.5">
        <span className="font-mono-num text-slate-400 shrink-0">{num}</span>
        <span>{text}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      </Label>
      <div className="pl-7">{children}</div>
    </div>
  );
}

export default function HvacAssessmentDialog({ open, onOpenChange, properties, onCreated }) {
  const [propertyId, setPropertyId] = useState("");
  const [a, setA] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setA(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId || !a.system_type || !a.severity) { toast.error("Please answer required questions"); return; }
    setBusy(true);
    try {
      await api.post("/maintenance", {
        property_id: propertyId,
        title: "HVAC Failure Assessment",
        description: `Severity: ${a.severity}. System: ${a.system_type}. ${a.failure_description || ""}`.trim(),
        priority: SEVERITY_TO_PRIORITY[a.severity] || "medium",
        assessment_type: "hvac",
        assessment: a,
        photo_paths: a.photo_paths || [],
      });
      toast.success("Assessment submitted");
      onOpenChange(false); setA(blank); setPropertyId("");
      onCreated?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hvac-assessment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ThermometerSun className="h-5 w-5 text-orange-600" />HVAC Failure Assessment</DialogTitle></DialogHeader>
        <Card className="bg-amber-50 border-amber-200 p-3 mb-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertOctagon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>For extreme indoor temperatures (≤55°F or ≥90°F) please call the landlord directly in addition to this form.</span>
        </Card>
        <form onSubmit={submit} className="space-y-2">
          <div className="space-y-1.5 pb-2 border-b border-slate-100">
            <Label className="text-xs">Property *</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="ha-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Q num="1" text="System type" required>
            <Select value={a.system_type} onValueChange={(v) => set("system_type", v)}>
              <SelectTrigger data-testid="ha-system"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Heating">Heating (furnace / boiler)</SelectItem>
                <SelectItem value="Cooling">Cooling (A/C / heat pump)</SelectItem>
                <SelectItem value="Both">Both / central HVAC</SelectItem>
                <SelectItem value="Thermostat">Thermostat only</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Q>
          <Q num="2" text="Is the system completely non-functional?">
            <YesNo testid="ha-working" value={a.is_working} onChange={(v) => set("is_working", v)} />
          </Q>
          <Q num="3" text="Severity" required>
            <Select value={a.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="ha-severity"><SelectValue placeholder="Select severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Severe">Severe (no heat in winter / no A/C in extreme heat)</SelectItem>
                <SelectItem value="Medium">Medium (intermittent / inadequate)</SelectItem>
                <SelectItem value="Low">Low (noisy / minor issue)</SelectItem>
              </SelectContent>
            </Select>
          </Q>
          <Q num="4" text="Date & time noticed"><Input type="datetime-local" value={a.noticed_at} onChange={(e) => set("noticed_at", e.target.value)} /></Q>
          <Q num="5" text="Current indoor temperature (°F)"><Input type="number" value={a.indoor_temp_f} onChange={(e) => set("indoor_temp_f", e.target.value)} className="w-32" placeholder="e.g. 62" /></Q>
          <Q num="6" text="System age (years, approximate)"><Input type="number" value={a.system_age_years} onChange={(e) => set("system_age_years", e.target.value)} className="w-32" /></Q>
          <Q num="7" text="Last maintenance date (if known)"><Input type="date" value={a.last_maintenance_date} onChange={(e) => set("last_maintenance_date", e.target.value)} /></Q>
          <Q num="8" text="Error code displayed on the unit / thermostat"><Input value={a.error_code} onChange={(e) => set("error_code", e.target.value)} placeholder="e.g. E04, blinking 3x red" /></Q>
          <Q num="9" text="Describe the failure">
            <Textarea rows={3} value={a.failure_description} onChange={(e) => set("failure_description", e.target.value)} placeholder="e.g. Fan runs but no heat; thermostat unresponsive…" />
          </Q>
          <Q num="10" text="Did the tenant contact the Rental Insurance Company?">
            <YesNo testid="ha-insurance" value={a.contacted_insurance} onChange={(v) => set("contacted_insurance", v)} />
          </Q>
          <Q num="10.0" text="Rep name, claim #, amount covered" gate={a.contacted_insurance === "Yes"}>
            <Textarea rows={2} value={a.insurance_details} onChange={(e) => set("insurance_details", e.target.value)} />
          </Q>
          <Q num="10.1" text="Action required — contact insurance, record rep, claim #, date/time, response" gate={a.contacted_insurance === "No"}>
            <Textarea rows={2} value={a.insurance_followup} onChange={(e) => set("insurance_followup", e.target.value)} />
          </Q>
          <Q num="11" text="Photos of the unit / error display (optional but recommended)">
            <PhotoUploader value={a.photo_paths} onChange={(v) => set("photo_paths", v)} testid="ha-photo" />
          </Q>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="ha-submit" type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800">{busy ? "Submitting…" : "Submit Assessment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
